<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span class="headline">{{ $t('uncoveredLogCombinations') }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh hideFullscreen/>
    </v-card-title>

    <v-card-text class="mt-4">
      <DetailCard
      v-for="(activity, index) in gap.activities"
      :key="`${activity.activityName}-${index}`"
      :title="activity.activityName"
      :elementClass="index > 0 ? 'mt-4' : ''"
      :fillHeight="false">
        <template #title.adjacent>
          <span v-if="activity.rateSheetDefinitionName" class="caption grey--text ml-2">
            {{ activity.rateSheetDefinitionName }}
          </span>
        </template>

        <v-data-table
        :headers="headers"
        :items="activity.combinations"
        dense
        hide-default-footer
        :items-per-page="-1"
        class="elevation-0">
          <template #item.gradeName="{ item }">
            <span>{{ item.gradeName || $t('notSpecified') }}</span>
          </template>
          <template #item.endUseSortName="{ item }">
            <span>{{ item.endUseSortName || $t('notSpecified') }}</span>
          </template>
          <template #item.previousLine="{ item }">
            <Icon
            v-if="item.previousLine"
            icon="mdi-information-outline"
            iconColor="info"
            :small="false"
            margin=""
            :tooltipText="previousLineTooltip(item.previousLine)"/>
            <span v-else class="caption grey--text">{{ $t('noPreviousRateLine') }}</span>
          </template>
        </v-data-table>

        <span
        v-if="activity.totalCombinations > activity.combinations.length"
        class="caption grey--text">
          {{ $t('showingUncoveredCombinations', { shown: activity.combinations.length, total: activity.totalCombinations }) }}
        </span>
      </DetailCard>

      <span class="body-2 d-block mt-4">{{ instructions }}</span>
    </v-card-text>
  </v-card>
</template>

<script>
import { formatMoney } from '@/utils/NumericMutations'

export default {
  name: 'UncoveredLogCombinationsDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    gap: {
      type: Object,
      required: true
    },
    fixOnDefinition: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    instructions () {
      return this.fixOnDefinition
        ? this.$t('uncoveredLogCombinationsDefinitionInstructions')
        : this.$t('uncoveredLogCombinationsRateSheetInstructions')
    },

    headers () {
      return [
        { text: this.$t('product'), value: 'productName' },
        { text: this.$t('species'), value: 'speciesName' },
        { text: this.$t('grade'), value: 'gradeName' },
        { text: this.$t('endUseSort'), value: 'endUseSortName' },
        { text: this.$t('length'), value: 'length', align: 'right' },
        { text: this.$t('diameter'), value: 'diameter', align: 'right' },
        { text: this.$t('previousRateLine'), value: 'previousLine', sortable: false, align: 'center', width: '150px' }
      ]
    }
  },

  methods: {
    previousLineTooltip (previousLine) {
      const criteria = [
        [this.$t('speciesGroup'), previousLine.speciesGroupLabel],
        [this.$t('lengthGroup'), previousLine.lengthGroupLabel],
        [this.$t('diameterGroup'), previousLine.diameterGroupLabel],
        [this.$t('grade'), previousLine.gradeName],
        [this.$t('endUseSort'), previousLine.endUseSortName]
      ].filter(([, value]) => value).map(([label, value]) => `${label}: ${value}`)

      return `${criteria.join(' · ')} · ${this.$t('rate')}: ${formatMoney(previousLine.rate)}`
    }
  }
}
</script>
